import { apiFetch } from "@/lib/api-fetch";
import { SUPABASE_URL } from "@/integrations/supabase/client";

/**
 * Cliente dos endpoints `/agents/portfolio/*` de completude/batch. Backend
 * exige JWT válido; chamadas de não-admin caem no gate da tela.
 */
const BASE = `${SUPABASE_URL}agents/portfolio`;

export interface CoverageSinal {
  key: string;
  label: string;
  filled: number;
  total: number;
  pct: number;
}

/** Etapa do funil de uso da base (base → visualizado → selecionado → shortlist → alocado). */
export interface FunilEtapa {
  key: string;
  label: string;
  count: number;
  /** % da base ativa. */
  pct_base: number;
  /** % da etapa anterior (conversão); null na primeira etapa. */
  pct_prev: number | null;
}

/** Oferta x demanda por área (base útil). Join por rótulo normalizado = direcional. */
export interface AreaDemanda {
  area: string;
  freelancers: number;
  vagas: number;
  alocacoes: number;
}

/** Footprint all-time de alocação (fonte real: controle_projetos), pra contextualizar o funil. */
export interface AllocationFootprint {
  ativos: number;
  total: number;
  projetos: number;
  desde_ano: number;
}

export type LadoReprovacao = "corisco" | "cliente" | "sem_motivo";

/** Uma linha de vagas reprovadas: quem reprovou, o motivo e a verba máxima somada (centavos). */
export interface MotivoReprovacao {
  quem: LadoReprovacao;
  motivo: string;
  vagas: number;
  verba_maxima_cents: number;
}

/** Vagas perdidas na janela (12 meses): só o lado "corisco" é perda atribuível à base ou à operação. */
export interface DemandaPerdida {
  janela_meses: number;
  total_vagas: number;
  aprovadas: number;
  reprovadas: number;
  /** Reprovadas com motivo preenchido (taxa de preenchimento = com_motivo / reprovadas). */
  com_motivo: number;
  motivos: MotivoReprovacao[];
}

export interface PortfolioCoverage {
  total_freelancers: number;
  sinais: CoverageSinal[];
  funil: FunilEtapa[];
  areas: AreaDemanda[];
  alocacao_footprint: AllocationFootprint;
  /** Opcional enquanto o backend em prod pode ser anterior ao campo (version skew). */
  demanda_perdida?: DemandaPerdida;
  portfolio: {
    analyzed: number;
    skipped_total: number;
    skipped_by_reason: Record<string, number>;
    failed: number;
    pending: number;
  };
}

export type BatchStatus = "running" | "done" | "error" | "paused" | "cancelled";

export interface BatchRun {
  id: string;
  status: BatchStatus;
  target: number;
  processed: number;
  ok_count: number;
  fail_count: number;
  started_at: string;
  updated_at: string;
  finished_at: string | null;
  error: string | null;
  /** Sinal de controle pendente lido pelo worker. */
  control: "pause" | "cancel" | null;
  /** Custo de IA (USD) acumulado nesta run. */
  cost_usd: number;
  prompt_tokens: number;
  completion_tokens: number;
  /** Itens que de fato chamaram o vision (denominador da média/previsão). */
  paid_count: number;
}

export interface LatestRunPayload {
  run: BatchRun | null;
  /** Pendentes restantes na base. */
  pending: number;
  /** Previsão do custo final (USD), ou null quando ainda não há amostra paga. */
  cost_projection: number | null;
}

export type BatchAction = "pause" | "resume" | "cancel";

export function getCoverage(): Promise<PortfolioCoverage> {
  return apiFetch<PortfolioCoverage>(`${BASE}/coverage`);
}

export function getLatestRun(): Promise<LatestRunPayload> {
  return apiFetch<LatestRunPayload>(`${BASE}/batch-run/latest`);
}

export function startBatch(): Promise<{ already_running: boolean; run: BatchRun | null }> {
  return apiFetch(`${BASE}/batch-run`, { method: "POST" });
}

export function controlBatch(runId: string, action: BatchAction): Promise<LatestRunPayload> {
  return apiFetch(`${BASE}/batch-run/${runId}/control?action=${action}`, { method: "POST" });
}
